import { z } from 'zod'
import type { DashboardUser } from '../auth/auth-adapter'
import { getEnvironment } from '../../lib/environment'
import { createApiClient } from '../../services/api/client'

const fingerprint = z.union([
  z.literal('MISSING'),
  z.string().regex(/^[a-f0-9]{64}$/),
])
export const reminderPolicySchema = z
  .object({
    schemaVersion: z.literal(1),
    tenantUid: z.string().min(1),
    revision: z.number().int().nonnegative(),
    configFingerprint: fingerprint,
    enabled: z.boolean(),
    entitled: z.boolean(),
    effectiveState: z.enum(['ON', 'OFF', 'STOPPING', 'UNAVAILABLE']),
  })
  .strict()
export type ReminderPolicy = z.infer<typeof reminderPolicySchema>
export type ReminderPolicyRepository = {
  get: (tenantId: string, signal?: AbortSignal) => Promise<ReminderPolicy>
  update: (policy: ReminderPolicy, enabled: boolean) => Promise<ReminderPolicy>
}

export function createReminderPolicyRepository({
  getUser,
  baseUrl = getEnvironment().onboardingApiBaseUrl,
  fetchImplementation,
}: {
  getUser: () => DashboardUser | null
  baseUrl?: string
  fetchImplementation?: typeof fetch
}): ReminderPolicyRepository {
  const client = createApiClient({ baseUrl, getUser, fetchImplementation })
  async function request(
    tenantId: string,
    method: 'POST' | 'PUT',
    body: object,
    signal?: AbortSignal
  ) {
    const result = await client.request('/v1/settings/appointment-reminders', {
      method,
      body: { tenantUid: tenantId, ...body },
      responseSchema: reminderPolicySchema,
      signal,
    })
    if (result.tenantUid !== tenantId)
      throw new Error('Reminder policy tenant mismatch')
    return result
  }
  return {
    get: (tenantId, signal) => request(tenantId, 'POST', {}, signal),
    update: (policy, enabled) =>
      request(policy.tenantUid, 'PUT', {
        sourceTenantUid: policy.tenantUid,
        expectedRevision: policy.revision,
        expectedFingerprint: policy.configFingerprint,
        enabled,
      }),
  }
}
